import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/auth-context';
import { api } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ShieldCheck, KeyRound, AlertTriangle, Check, X } from 'lucide-react';
import { BRAND } from '../lib/brand';

function StrengthRule({ ok, label }) {
  return (
    <div
      className={`flex items-center gap-2 text-xs ${
        ok ? 'text-emerald-700' : 'text-muted-foreground'
      }`}
      data-testid={`pw-rule-${ok ? 'ok' : 'pending'}`}
    >
      {ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5 opacity-60" />}
      <span>{label}</span>
    </div>
  );
}

export default function ChangePasswordPage() {
  const { t } = useTranslation();
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const forced = !!user?.must_change_password;

  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  // Live strength rules
  const ruleLength = newPassword.length >= 8;
  const ruleUpper = /[A-Z]/.test(newPassword);
  const ruleDigit = /\d/.test(newPassword);
  const ruleDifferent = newPassword.length > 0 && newPassword !== currentPassword;
  const rulesPassed = ruleLength && ruleUpper && ruleDigit && ruleDifferent;
  const confirmsMatch = confirmPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = rulesPassed && confirmsMatch && currentPassword.length > 0 && !loading;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      const r = await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setUser(r.data.user);
      toast.success(t('changePwd.success', 'Password aggiornata con successo. Benvenuto!'));
      // After forced change → land on dashboard. From settings flow → go back.
      const redirectTo = loc.state?.from?.pathname || '/app';
      nav(redirectTo, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('changePwd.failed', 'Cambio password fallito'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-[var(--surface-1)]">
      <div
        className="relative hidden lg:block"
        style={{
          backgroundImage: `url('${BRAND.heroLombok}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="change-pw-hero"
      >
        <div className="absolute inset-0 gp-hero-overlay" />
        <div className="relative z-10 h-full flex flex-col justify-end p-10 text-[hsl(var(--primary-foreground))]">
          <img
            src={BRAND.greenParadiseLogoSmall}
            alt="Green Paradise"
            className="h-14 w-auto mb-4 bg-white/10 rounded-md p-2 self-start"
          />
          <div className="font-serif text-5xl leading-tight">{t('changePwd.heroTitle', 'Sicurezza account')}</div>
          <div className="mt-2 text-sm tracking-[0.25em] uppercase opacity-80">
            CRM · HCF Property
          </div>
          <p className="mt-6 max-w-md text-sm opacity-90">
            {t('changePwd.heroDesc', 'Per proteggere i dati dei tuoi lead e dei tuoi clienti, ogni agente deve impostare una password personale al primo accesso.')}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <Card className="w-full max-w-md border-border" data-testid="change-pw-card">
          <CardContent className="p-8">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-7 h-7 text-[hsl(var(--primary))]" />
              <div>
                <div className="font-serif text-xl text-[hsl(var(--primary))]">
                  {forced ? t('changePwd.setTitle', 'Imposta la tua password') : t('changePwd.changeTitle', 'Cambia password')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {forced
                    ? t('changePwd.setSubtitle', 'Primo accesso — è obbligatorio sostituire la password provvisoria.')
                    : t('changePwd.changeSubtitle', 'Aggiorna la tua password di accesso al CRM.')}
                </div>
              </div>
            </div>

            {forced && (
              <div
                className="mt-4 mb-5 flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs"
                data-testid="forced-change-banner"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  {t('changePwd.forcedBanner', 'Stai usando la password provvisoria assegnata dal sistema. Per continuare devi impostarne una personale.')}
                </span>
              </div>
            )}

            <form onSubmit={submit} className="space-y-4 mt-4">
              <div className="space-y-1.5">
                <Label htmlFor="current_password">{t('changePwd.currentLabel', 'Password attuale')}</Label>
                <Input
                  id="current_password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  data-testid="change-pw-current-input"
                  placeholder={forced ? t('changePwd.currentPlaceholder', 'La password provvisoria che hai ricevuto') : '••••••••'}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="new_password">{t('changePwd.newLabel', 'Nuova password')}</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  data-testid="change-pw-new-input"
                  placeholder={t('changePwd.newPlaceholder', 'Scegli una password sicura')}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirm_password">{t('changePwd.confirmLabel', 'Conferma nuova password')}</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  data-testid="change-pw-confirm-input"
                  placeholder={t('changePwd.confirmPlaceholder', 'Ripeti la nuova password')}
                />
                {confirmPassword.length > 0 && !confirmsMatch && (
                  <div className="text-xs text-rose-600 mt-1">{t('changePwd.mismatch', 'Le due password non coincidono')}</div>
                )}
              </div>

              <div className="rounded-md border border-border bg-[var(--surface-2)] p-3 space-y-1.5">
                <div className="text-xs font-medium text-[hsl(var(--primary))] flex items-center gap-1.5 mb-1">
                  <KeyRound className="w-3.5 h-3.5" />
                  {t('changePwd.requirements', 'Requisiti password')}
                </div>
                <StrengthRule ok={ruleLength} label={t('changePwd.ruleLength', 'Almeno 8 caratteri')} />
                <StrengthRule ok={ruleUpper} label={t('changePwd.ruleUpper', 'Una lettera maiuscola')} />
                <StrengthRule ok={ruleDigit} label={t('changePwd.ruleDigit', 'Un numero')} />
                <StrengthRule ok={ruleDifferent} label={t('changePwd.ruleDifferent', 'Diversa dalla password attuale')} />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={!canSubmit}
                data-testid="change-pw-submit-button"
              >
                {loading ? t('changePwd.saving', 'Salvataggio…') : forced ? t('changePwd.setAndLogin', 'Imposta password e accedi') : t('changePwd.update', 'Aggiorna password')}
              </Button>

              {forced && (
                <button
                  type="button"
                  onClick={() => {
                    logout();
                    nav('/login', { replace: true });
                  }}
                  className="w-full text-xs text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors mt-2 underline-offset-4 hover:underline"
                  data-testid="change-pw-logout-button"
                >
                  {t('changePwd.backToLogin', 'Annulla e torna al login')}
                </button>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
